import { PageController } from '@client/components/_core/layout/PageController';
import { modal } from '@client/components/_ui/feedback/modal';
import { RiskAppetiteLabel } from '@client/components/risks/RiskAppetiteLabel';
import {
    type RiskAppetiteStatementAction,
    RiskAppetiteStatementAdd,
} from '@client/components/risks/RiskAppetiteStatementAdd';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { SquareMinus, SquarePlus } from 'lucide-react';
import { useState } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/console/$orgId/risk/ras/')({
    staticData: {
        ui: {
            title: 'Risk appetite statement',
            subtitle: 'Manage risk appetites for your risk categories',
        },
    },
    component: RiskAppetiteStatementSearch,
});

type QueryParams = RouterInputs['risk']['appetiteStatement']['getRiskCategoryTreeWithAppetites'];
type RiskCategoryData = RouterOutputs['risk']['appetiteStatement']['getRiskCategoryTreeWithAppetites'];
type RiskCategoryRow = NonNullable<RiskCategoryData>['rows'][number];
type OrderByType = NonNullable<QueryParams['orderBy']>;

function RiskAppetiteStatementSearch() {
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const [action, setAction] = useState<RiskAppetiteStatementAction>(['none']);
    const { can } = usePermissions();
    const canManage = can('risk:update');

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'asc' },
        where: undefined,
    });

    const { data, isPending, refetch } = trpc.risk.appetiteStatement.getRiskCategoryTreeWithAppetites.useQuery(input);

    const columnHelper = createColumnHelper<RiskCategoryRow, OrderByType>();

    const columns = [
        columnHelper.display({
            id: 'category',
            header: 'Risk category',
            cell: ({ row }) => row.category.name,
            hideable: false,
        }),
        columnHelper.display({
            id: 'riskAppetite',
            header: 'Risk appetite',
            cell: ({ row }) => (
                <RiskAppetiteLabel
                    description={row.riskAppetite?.appetite?.description || ''}
                    color={row.riskAppetite?.appetite?.color}
                    name={row.riskAppetite?.appetite?.name}
                    isDeleted={row.riskAppetite?.isDeleted}
                />
            ),
            className: 'whitespace-nowrap w-0 px-5',
        }),
        columnHelper.display({
            id: 'summary',
            header: 'Summary',
            cell: ({ row }) => row.riskAppetite?.summary || '',
        }),
    ];

    // A row is interactive when it navigates to an existing statement, or when
    // the caller can create one for an empty category.
    const isRowInteractive = (record: RiskCategoryRow) =>
        Boolean(record?.riskAppetite?.id) || (canManage && Boolean(record?.category?.id));

    const handleRowClick = (record: RiskCategoryRow, e: React.MouseEvent) => {
        if (record?.riskAppetite?.id) {
            navigate(
                {
                    to: '/console/$orgId/risk/ras/details/$id',
                    params: {
                        orgId,
                        id: record.riskAppetite.id,
                    },
                },
                e,
            );
        } else if (record?.category?.id && canManage) {
            if (record.categoryLevel === 1) {
                setAction(['add', record.category.id, record.categoryHierarchy.map((c) => c.name)]);
                return;
            }
            const ancestors = record.categoryHierarchy.slice(0, -1);

            for (let i = 0; i < ancestors.length; i++) {
                const ancestorId = ancestors[i].id;
                const ancestorName = ancestors[i].name;
                const ancestorLevel = i + 1;

                const findAncestor = (categories: RiskCategoryRow[]): RiskCategoryRow | undefined => {
                    for (const category of categories) {
                        if (category.categoryLevel === ancestorLevel && category.category.id === ancestorId) {
                            return category;
                        }
                        if (category.children.length > 0) {
                            const found = findAncestor(category.children);
                            if (found) return found;
                        }
                    }
                    return undefined;
                };

                const ancestor = findAncestor(data?.rows || []);

                if (!ancestor?.riskAppetite || ancestor.riskAppetite.isDeleted) {
                    modal.warning({
                        title: 'Action Required',
                        content: `Before assigning a risk appetite to "${record.category.name}", please first define a risk appetite for the parent category "${ancestorName}". Risk appetites must be assigned in hierarchical order.`,
                    });
                    return;
                }
            }

            setAction(['add', record.category.id, record.categoryHierarchy.map((c) => c.name)]);
        }
    };

    const tableProps = usePrismaTable<RiskCategoryRow, QueryParams['where'], OrderByType>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'key',
        queryParams: input,
        onParamsChange: setInput,
        pagination: { position: 'both' },
        emptyState: {
            noRecordsTitle: 'No risk categories yet',
            noRecordsDescription: 'Risk categories set up in risk configuration appear here.',
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        expandable: {
            expandIcon: ({ expanded, onExpand, record }) => (
                <div
                    className="-m-2 inline-block p-4"
                    onClick={(e) => {
                        onExpand(record, e);
                        e.stopPropagation();
                    }}
                >
                    {expanded ? (
                        <span className="anticon">
                            <SquareMinus className="text-muted-foreground" size={16} />
                        </span>
                    ) : (
                        <span className="anticon">
                            <SquarePlus className="text-muted-foreground" size={16} />
                        </span>
                    )}
                </div>
            ),
            expandIconColumnIndex: 0,
        },
    });

    return (
        <PageController route={Route}>
            <PrismaTable
                {...tableProps}
                onRow={(record) =>
                    match(isRowInteractive(record))
                        .with(true, () => ({
                            className: 'cursor-pointer',
                            onClick: (e: React.MouseEvent) => handleRowClick(record, e),
                        }))
                        .otherwise(() => ({}))
                }
            />

            <RiskAppetiteStatementAdd
                action={action}
                onClose={() => {
                    setAction(['none']);
                    void refetch();
                }}
            />
        </PageController>
    );
}
